import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

const LOGO_URL = "/images/basic-united-logo-light.png";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/', label: { de: 'Startseite', en: 'Home' } },
    { href: '/ueber-uns', label: { de: 'Über Uns', en: 'About Us' } },
    { href: '/unternehmen', label: { de: 'Unternehmen', en: 'Companies' } },
    { href: '/leistungen/strategische-distribution', label: { de: 'Leistungen', en: 'Services' } },
    { href: '/karriere', label: { de: 'Karriere', en: 'Careers' } },
    { href: '/kontakt', label: { de: 'Kontakt', en: 'Contact' } },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#0A1220]/95 backdrop-blur-md shadow-lg shadow-black/20' 
          : 'bg-[#0F1A2E]'
      }`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Navigation only - no logo */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.de.toLowerCase().replace(/\s+/g, '-')}`}
                className={`nav-link whitespace-nowrap ${isActive(link.href) ? 'active text-white' : ''}`}
              >
                {link.label[language]}
              </Link>
            ))}
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher"
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/20 hover:border-[#F5A623] hover:text-[#F5A623] transition-colors text-sm font-medium text-white"
              title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}
            >
              <Globe size={16} />
              <span>{language === 'de' ? 'EN' : 'DE'}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0A1220] border-t border-[#1e3a5f]"
            data-testid="mobile-menu"
          >
            <div className="container-main py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-link-${link.label.de.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block py-3 nav-link ${isActive(link.href) ? 'active text-white' : ''}`}
                >
                  {link.label[language]}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <button
                onClick={toggleLanguage}
                data-testid="mobile-language-switcher"
                className="flex items-center gap-2 py-3 text-white hover:text-[#F5A623] transition-colors font-medium"
              >
                <Globe size={18} />
                <span>{language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
